import React from 'react';
import { ConfirmationToken, StripePaymentElementOptions } from '@stripe/stripe-js';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import {
	createPaymentIntent,
	completePayment,
	PaymentProviderTransactionDetails,
} from 'Services/Api/_HumanWritten/PaymentService';
import { usePaymentCheckOut } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import If from 'Views/Components/If/If';
import { LottieSpinner } from '../Lottie/LottieSpinner';
import { useCardFieldsFailureReporter } from './PaymentUnavailable';
import { shouldSimulatePaymentFailure } from './paymentFailureSimulation';
import { store } from 'Models/Store';
import paymentStore from 'Models/PaymentStore';

/**
 * Stripe-specific payment form. Owns the Stripe SDK touchpoints (<c>useStripe</c>,
 * <c>useElements</c>, <c>&lt;PaymentElement&gt;</c>, confirmation token creation, payment
 * confirmation) and exposes an imperative <c>submit</c> method to its parent so the parent
 * <c>CheckOutForm</c> can drive the submission flow alongside non-card payment methods.
 *
 * Lives under <c>Payment/</c> rather than <c>Payment/Providers/Stripe/</c> for now to keep
 * the existing folder structure flat. Square's equivalent (<c>SquareCheckoutForm</c>) lands
 * in Phase B and follows the same imperative-handle contract.
 */
export interface StripeCheckoutFormHandle {
	/**
	 * Submit a Stripe-based payment. If <c>savedPaymentMethodId</c> is provided, the user is
	 * paying with an existing saved card; otherwise we collect a fresh card via
	 * <c>&lt;PaymentElement&gt;</c> and create a new confirmation token.
	 *
	 * Returns true on success (the parent should then call <c>onSuccess</c>); false on
	 * failure (error has already been surfaced to the user).
	 */
	submit: (savedPaymentMethodId?: string) => Promise<boolean>;
}

export interface StripeCheckoutFormProps {
	tabIsLoading: boolean;
	paymentProcessing: boolean;
	disableContinueButton: boolean;
}

const StripeCheckoutForm = React.forwardRef<StripeCheckoutFormHandle, StripeCheckoutFormProps>(
	({ tabIsLoading, paymentProcessing }, ref) => {
		const stripe = useStripe();
		const elements = useElements();
		// Null if this form is ever rendered outside a provider chunk; the optional call below then
		// reports nowhere rather than throwing on a payment screen.
		const cardFieldsFailure = useCardFieldsFailureReporter();

		// Dev-only, armed from sessionStorage — see paymentFailureSimulation. Stripe will not fail
		// its own element on request, so the simulation reports the failure directly instead of
		// waiting for an `onLoadError` that a healthy sandbox never fires.
		React.useEffect(() => {
			if (shouldSimulatePaymentFailure('element')) {
				cardFieldsFailure?.reportElementFailure();
			}
		}, [cardFieldsFailure]);

		const {
			handleError,
			setMessage,
			setRefetchPrice,
			refetchPrice,
			setTabIsLoading,
			setDisableContinueButton,
			handleSurchargeUpdate,
			handleProviderNotLoaded,
			transactionId,
			eventBooking,
			inCheckIn,
			message,
			transactionDetails,
			onConfirm,
			disableContinueButton,
			totalPrice,
			paymentMethod,
		} = usePaymentCheckOut();

		const handleErrorLocal = (errorMessage: string | null) => {
			handleError(errorMessage);
			setTabIsLoading(false);
		};

		const createConfirmationToken = async (): Promise<ConfirmationToken | undefined> => {
			if (!stripe || !elements) {
				handleProviderNotLoaded();
				return undefined;
			}

			const { error: submitError } = await elements.submit();
			if (submitError && isNotNullOrUndefined(submitError?.message)) {
				handleErrorLocal(submitError.message);
				return undefined;
			}

			const { error, confirmationToken } = await stripe.createConfirmationToken({
				elements,
				params: {},
			});

			if (error) {
				handleErrorLocal(error.message ?? 'An unexpected error occurred.');
			}

			return confirmationToken;
		};

		const createNewPaymentIntent = async (
			confirmationToken?: ConfirmationToken,
			savedPaymentMethodId?: string,
		): Promise<PaymentProviderTransactionDetails | null> => {
			try {
				const paymentIntentResponse = await createPaymentIntent(
					transactionId,
					transactionDetails?.formActionUrl ?? '',
					eventBooking,
					inCheckIn,
					confirmationToken?.id,
					savedPaymentMethodId,
				);
				return paymentIntentResponse.data as PaymentProviderTransactionDetails;
			} catch (e: any) {
				const errorResponse = e?.response?.data as unknown as { message: string; refresh: boolean };
				if (errorResponse?.message?.includes('Your selected payment method has updated the surcharge')) {
					handleSurchargeUpdate(errorResponse.refresh);
				} else {
					handleError(errorResponse.message ?? 'An unexpected error occurred.');
				}
				setRefetchPrice(!refetchPrice);
				return null;
			}
		};

		const confirmPayment = async (
			clientSecret: string,
			confirmationToken?: ConfirmationToken,
		): Promise<boolean> => {
			if (!stripe || !elements || !clientSecret || isNotNullOrUndefined(message)) {
				handleProviderNotLoaded();
				return false;
			}

			const { error, paymentIntent } = await stripe.confirmPayment({
				clientSecret,
				confirmParams: {
					confirmation_token: confirmationToken?.id,
				},
				redirect: 'if_required',
			});

			if (error) {
				handleErrorLocal(error.message ?? 'An unexpected error occurred.');
				return false;
			}

			if (paymentIntent && paymentIntent.status === 'succeeded') {
				try {
					await completePayment(paymentIntent.id, inCheckIn);
					return true;
				} catch (e: any) {
					console.error('completePayment failed:', e?.response?.data ?? e);
					handleErrorLocal('Payment was processed but an error occurred. Please contact support.');
					return false;
				}
			}

			return false;
		};

		React.useImperativeHandle(ref, () => ({
			submit: async (savedPaymentMethodId?: string) => {
				console.log('[StripeCheckoutForm] submit start', {
					savedPaymentMethodId,
					hasStripe: !!stripe,
					hasElements: !!elements,
					message,
				});
				if (!stripe || !elements) {
					console.log('[StripeCheckoutForm] stripe/elements not loaded');
					handleProviderNotLoaded();
					return false;
				}

				if (savedPaymentMethodId) {
					// Saved card flow — no new confirmation token needed.
					if (message !== null) {
						console.log('[StripeCheckoutForm] saved card: message not null, aborting', message);
						handleProviderNotLoaded();
						return false;
					}
					const intent = await createNewPaymentIntent(undefined, savedPaymentMethodId);
					if (!isNotNullOrUndefined(intent)) {
						handleProviderNotLoaded();
						return false;
					}
					return confirmPayment(intent.paymentIntentClientSecret, undefined);
				}

				// Fresh card flow.
				console.log('[StripeCheckoutForm] fresh card: creating confirmation token');
				const confirmationToken = await createConfirmationToken();
				console.log('[StripeCheckoutForm] fresh card: confirmationToken result', {
					hasToken: !!confirmationToken,
					message,
				});
				if (message !== null || !confirmationToken) {
					console.log('[StripeCheckoutForm] fresh card: blocked', { message, hasToken: !!confirmationToken });
					handleProviderNotLoaded();
					return false;
				}
				console.log('[StripeCheckoutForm] fresh card: creating payment intent');
				const intent = await createNewPaymentIntent(confirmationToken);
				console.log('[StripeCheckoutForm] fresh card: intent result', {
					hasIntent: !!intent,
					clientSecret: intent?.paymentIntentClientSecret?.substring(0, 10),
				});
				if (!isNotNullOrUndefined(intent)) {
					handleProviderNotLoaded();
					return false;
				}
				return confirmPayment(intent.paymentIntentClientSecret, confirmationToken);
			},
		}));

		const paymentElementOptions = {
			layout: 'tabs',
			// Staff take payments on behalf of customers — hide Apple Pay and Google Pay so a staff
			// member doesn't accidentally charge their own wallet on the device they're using.
			...(store.isStaff ? { wallets: { applePay: 'never', googlePay: 'never' } } : {}),
		} as StripePaymentElementOptions;

		return (
			<div id="payment-container">
				<PaymentElement
					id="payment-element"
					className={tabIsLoading ? 'hidden' : ''}
					options={paymentElementOptions}
					onChange={change => {
						// Only let the card element drive the continue button while card is the active
						// method. Otherwise a late onChange (the element finishes loading after the staff
						// member has already switched methods) re-disables the button for a non-card method.
						if (paymentMethod === 'credit-card-option') {
							const shouldDisable = !change.complete;
							if (disableContinueButton !== shouldDisable) {
								setDisableContinueButton(shouldDisable);
							}
						}
						if (message !== null) {
							setMessage(null);
						}

						// Keep the Elements amount in sync with the selected payment method's
						// surcharge so wallet modals (Apple Pay / Google Pay) open with the
						// surcharge-inclusive total. The native wallet sheet reads Elements.amount
						// at the moment it opens — there's no server round-trip in between.
						if (elements && totalPrice != null) {
							const selectedType = (change.value as any)?.type as string | undefined;
							let surchargePercent = 0;
							if (selectedType === 'apple_pay') {
								surchargePercent = paymentStore.applePayFeePercentage;
							} else if (selectedType === 'google_pay') {
								surchargePercent = paymentStore.googlePayFeePercentage;
							}
							// Card / Link / other: the surcharge is recomputed server-side on
							// submit from the confirmation token, so the Elements amount can stay
							// at the base total — no wallet sheet is shown for those.
							const nextAmountCents = Math.trunc(totalPrice * (1 + surchargePercent / 100) * 100);
							elements.update({ amount: nextAmountCents });
						}
					}}
					onFocus={() => {
						if (message !== null) {
							setMessage(null);
						}
					}}
					onBlur={() => {
						if (message !== null) {
							setMessage(null);
						}
					}}
					onReady={() => {
						setTabIsLoading(false);
					}}
					// The SDK loaded but the element would not mount itself — a bad publishable key,
					// bad element options, or Stripe's API refusing it. Without this the failure is
					// silent: `onReady` never fires, nothing else watches the element, and the
					// customer is left looking at an empty box with no error and no way forward.
					//
					// Reported upward rather than rendered here, so the card slot stays the only thing
					// deciding what the card slot shows. Reporting unmounts this form, which is what
					// makes Try again a real remount of the element rather than a re-download of a
					// script the browser has already cached.
					onLoadError={({ error }) => {
						// eslint-disable-next-line no-console
						console.error('Stripe PaymentElement failed to load:', error?.message ?? error);
						// The element will never become ready, so nothing else clears this. Left set,
						// the pay button stays disabled and reading "Processing...".
						setTabIsLoading(false);
						cardFieldsFailure?.reportElementFailure();
					}}
				/>
				<If condition={tabIsLoading}>
					<LottieSpinner className="centered" />
				</If>
			</div>
		);
	},
);

export default StripeCheckoutForm;
